// The audience page's whole conversation with the backend.
//
// Kept out of the component because polling with an ETag, tolerating 304s, and
// telling "the room is gone" apart from "the network hiccuped" is logic worth
// testing on its own — and because the component should read as a UI, not as a
// protocol.

// In PRODUCTION this IS set — .env.production points it at api2.copresent.app,
// the Node service's own hostname. The plan to serve it as api.copresent.app/qa/*
// so no client knew a second host never shipped (Enterprise-only Cloudflare
// override), so the clicker calls that second host directly.
//
// The fallback to REACT_APP_API_URL is the local story: point it at the Node
// service's own port, or leave it unset and reach Q&A through the C# backend's
// dev proxy on the one host. Fallback, never a default — a value baked in here
// would quietly become a second production origin.
const API_URL =
  process.env.REACT_APP_QA_API_URL ?? process.env.REACT_APP_API_URL ?? "";

export interface AudienceQuestion {
  readonly id: string;
  readonly text: string;
  readonly authorName: string | null;
  readonly slideIndex: number | null;
  readonly votes: number;
  readonly answered: boolean;
  readonly pinned: boolean;
  readonly askedAt: string;
}

/**
 * Every failure the audience can hit, collapsed to what the page should DO
 * about it.
 *
 * The server deliberately returns the same 404 for an unknown code, an expired
 * one, a deck with no live session, and a room the host closed — telling a
 * stranger which codes exist would be a leak. So there is one `unavailable`
 * here rather than four, and the page says one honest thing.
 */
export type AudienceError =
  | { readonly kind: "unavailable" }
  | { readonly kind: "rate_limited"; readonly retryAfterSeconds: number }
  | { readonly kind: "rejected" }
  | { readonly kind: "offline" };

export class AudienceRequestError extends Error {
  constructor(readonly detail: AudienceError) {
    super(detail.kind);
  }
}

// A stable per-device id, so a vote can be deduped without an account.
//
// Trivially resettable by clearing storage, and that is fine: it is dedupe, not
// identity, and the server pairs it with the request IP for rate limiting so
// clearing it does not buy a fresh quota.
const VOTER_KEY = "copresentVoterId";

export const getVoterId = (): string => {
  try {
    const existing = window.localStorage.getItem(VOTER_KEY);
    if (existing) return existing;

    const minted =
      typeof crypto?.randomUUID === "function"
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    window.localStorage.setItem(VOTER_KEY, minted);
    return minted;
  } catch {
    // Private browsing with storage disabled. A per-session id still dedupes
    // within the page's lifetime, which is the whole visit for most people.
    return `v-${Math.random().toString(36).slice(2)}`;
  }
};

/**
 * The canonical form of a code, mirroring the service's `normaliseCode`.
 *
 * The service is forgiving about how a code is typed — case, spaces, dashes,
 * and the glyphs its alphabet excludes because they are indistinguishable (O
 * for zero, I and L for one). So /q/sq3jwa, /q/SQ3JWA and /q/SQ3-JWA are all
 * the same room, and the URL a scanner produces need not match the one someone
 * types.
 *
 * That forgiveness is exactly why this has to exist here too: anything the
 * client keys off the RAW route param gets a separate bucket per spelling. The
 * voted set below did, so arriving at the same room by a different spelling
 * re-armed every vote button — the server still refused the duplicate, so the
 * count jumped and snapped back on the next poll.
 *
 * Keep in step with api/src/qa/room-code.ts.
 */
export const normaliseRoomCode = (input: string): string =>
  input
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");

// Which questions this device has voted for. The server never says — that would
// let a caller probe who voted for what — so the client remembers its own.
//
// Keyed by the NORMALISED code; see normaliseRoomCode.
const VOTED_KEY = "copresentVotedQuestions";

export const readVoted = (code: string): Set<string> => {
  try {
    const raw = window.localStorage.getItem(`${VOTED_KEY}:${normaliseRoomCode(code)}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : []);
  } catch {
    return new Set();
  }
};

export const rememberVote = (code: string, questionId: string): void => {
  try {
    const voted = readVoted(code);
    voted.add(questionId);
    window.localStorage.setItem(
      `${VOTED_KEY}:${normaliseRoomCode(code)}`,
      JSON.stringify(Array.from(voted)),
    );
  } catch {
    // Storage unavailable; the vote still counted server-side, the UI just
    // forgets it across a reload. Not worth failing the interaction over.
  }
};

async function parse(response: Response): Promise<never> {
  if (response.status === 404) throw new AudienceRequestError({ kind: "unavailable" });
  if (response.status === 429) {
    const header = Number.parseInt(response.headers.get("retry-after") ?? "", 10);
    throw new AudienceRequestError({
      kind: "rate_limited",
      retryAfterSeconds: Number.isFinite(header) && header > 0 ? header : 30,
    });
  }
  throw new AudienceRequestError({ kind: "rejected" });
}

export interface QuestionsSnapshot {
  /** Null means "unchanged since the etag you sent" — keep what you have. */
  readonly questions: AudienceQuestion[] | null;
  readonly etag: string | null;
}

/**
 * Fetch the room's questions, sending the previous ETag so an unchanged list
 * costs a 304 rather than a payload.
 *
 * A poll every few seconds from a room full of phones is the single heaviest
 * thing this feature does; the ETag is what makes it cheap for them, and the
 * server's short cache is what makes it cheap for us.
 */
export async function fetchQuestions(
  code: string,
  etag: string | null,
  signal?: AbortSignal,
): Promise<QuestionsSnapshot> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/qa/rooms/${encodeURIComponent(code)}/questions`, {
      headers: etag ? { "If-None-Match": etag } : {},
      signal: signal ?? null,
    });
  } catch {
    throw new AudienceRequestError({ kind: "offline" });
  }

  if (response.status === 304) {
    return { questions: null, etag };
  }
  if (!response.ok) return parse(response);

  const body = (await response.json()) as { questions?: AudienceQuestion[] };
  return {
    questions: Array.isArray(body.questions) ? body.questions : [],
    etag: response.headers.get("etag"),
  };
}

export async function askQuestion(
  code: string,
  text: string,
  name: string | null,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/qa/rooms/${encodeURIComponent(code)}/questions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, name, voterId: getVoterId() }),
    });
  } catch {
    throw new AudienceRequestError({ kind: "offline" });
  }

  if (!response.ok) return parse(response);
}

export async function voteForQuestion(code: string, questionId: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(
      `${API_URL}/qa/rooms/${encodeURIComponent(code)}/questions/${encodeURIComponent(
        questionId,
      )}/vote`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voterId: getVoterId() }),
      },
    );
  } catch {
    throw new AudienceRequestError({ kind: "offline" });
  }

  if (!response.ok) return parse(response);
}
